import { EllipsisVerticalIcon } from "@heroicons/react/16/solid";
import posthog from "posthog-js";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  FormControlLabel,
  MenuItem,
  MenuList,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
} from "@mui/material";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import { GridColDef, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { GridRowSelectionModel } from "@mui/x-data-grid/models/gridRowSelectionModel";

import ActionToolbar from "../../components/ActionToolbar";
import DataGridPro from "../../components/DataGridPro";
import DataGridProToolbar from "../../components/DataGridProToolbar";
import DeleteActionDialog, { Entry } from "../../components/DeleteActionDialog";
import Header from "../../components/Header";
import Page from "../../components/Page";
import Quota from "../../components/Quota";
import { ConfirmationDialog } from "../../components/confirmation-dialog";
import SvgIcon from "../../components/styled/SvgIcon";
import useFeatureFlag from "../../hooks/use-feature-flag";
import { usePopover } from "../../hooks/use-popover";
import { useSavedColumnWidths } from "../../hooks/use-saved-column-widths";
import { updatedAt } from "../../utils/data-grid-col-defs";
import { DisplayJWT } from "../settings/components/DisplayJWT";
import ActionToolbarDialogs from "./components/ActionToolbarDialogs";
import ClusterOnboardingWizard from "@/components/ClusterOnboardingWizard";
import { HostedChip } from "@/components/HostedChip";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useQuota from "@/hooks/use-quota";
import useServerNotifications from "@/hooks/use-server-notifications";
import { useSelector } from "@/store";
import {
  Cluster,
  ClusterFlavor,
  CreateClusterResponse,
  useCreateClusterMutation,
  useDeleteClusterMutation,
  useGenerateSubdomainNameQuery,
  useGetClusterQuery,
  useGetOrganizationQuery,
  useListClustersQuery,
  usePatchSettingsMutation,
  useRotateClusterTokenMutation,
  useUpdateClusterMutation,
} from "@/store/pomeriumApi";

const defaultColumns = JSON.stringify({
  id: false,
});

const List: FC = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);
  const [showDelete, setShowDelete] = useState(false);
  const [clusterId, setClusterId] = useState("");
  const actionsPopover = usePopover();
  const anchorRef = useRef<HTMLElement | null>(null);
  const { organizationId } = useSelector((state) => state.current);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<string>(
    localStorage.getItem("clustersColumns") || defaultColumns,
  );
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [rotateIdentity] = useRotateClusterTokenMutation();
  const [newIdentity, setNewIdentity] = useState<string>("");
  const { refetch: refetchQuota } = useQuota("clusters");
  const supportsHostedClusters = useFeatureFlag("hosted-clusters", false);

  const handleRotatingIdentity = async () => {
    setConfirmRotate(false);
    const newIdentity = await rotateIdentity({
      organizationId,
      clusterId,
    }).unwrap();
    posthog.capture("cluster_identity_rotated", { rotated: clusterId });
    setNewIdentity(newIdentity.refreshToken);
  };

  const { mutateColumnWidths, onColumnWidthChange } =
    useSavedColumnWidths("clusters");

  const [deleteCluster] = useDeleteClusterMutation();

  async function handleMultiDelete() {
    await Promise.all(
      selected.map((id) =>
        deleteCluster({
          organizationId,
          clusterId: id.toString(),
        }),
      ),
    );
    refetchQuota();
    posthog.capture("clusters_deleted", { deleted: selected.toString() });
  }

  const deleteOne = () => {
    setSelected([clusterId]);
    setShowDelete(true);
    actionsPopover.handleClose();
  };

  const onCloseDeleteDialog = () => {
    setShowDelete(false);
    setSelected([]);
  };

  const {
    data: clusters,
    isLoading,
    isFetching,
  } = useListClustersQuery(organizationId, { skip: !organizationId });

  let columns: GridColDef<Cluster>[] = [
    {
      display: "flex",
      headerName: "Id",
      field: "id",
      flex: 1,
    },
    {
      display: "flex",
      headerName: "Cluster Name",
      field: "name",
      flex: 2,
      renderCell: (params) => {
        const name = params?.row?.name || "";
        const isHosted = params?.row?.flavor === "hosted";
        return (
          <div className={"grid grid-cols-[1fr_auto] gap-1 items-center"}>
            <span>{name}</span>
            {isHosted ? <HostedChip /> : null}
          </div>
        );
      },
    },
    {
      display: "flex",
      headerName: "Domain",
      field: "fqdn",
      flex: 2,
    },
    {
      display: "flex",
      headerName: "IP",
      field: "autoDetectIpAddress",
      flex: 1,
      valueGetter: (_value, row) => {
        return row?.manualOverrideIpAddress || row?.autoDetectIpAddress;
      },
    },
    updatedAt,
    {
      display: "flex",
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: function action(params) {
        return (
          <Grid
            container
            spacing={0}
            direction="column"
            alignItems="center"
            justifyContent="center"
          >
            <IconButton
              data-flavor={params.row.flavor}
              onClick={(e) => {
                anchorRef.current = e.currentTarget;
                setClusterId(params.row.id);
                actionsPopover.handleOpen();
              }}
              sx={{ width: 4 }}
            >
              <SvgIcon color="disabled">
                <EllipsisVerticalIcon />
              </SvgIcon>
            </IconButton>
          </Grid>
        );
      },
    },
  ];
  columns = mutateColumnWidths(columns);

  const CustomToolbar: FC = useCallback(
    () => (
      <Stack direction="row" justifyContent="space-between">
        <DataGridProToolbar>
          {selected.length >= 1 && (
            <ActionToolbar
              selectedIds={selected}
              onDelete={() => setShowDelete(true)}
            />
          )}
        </DataGridProToolbar>
        <GridToolbarQuickFilter variant="outlined" size="small" sx={{ m: 1 }} />
      </Stack>
    ),
    [JSON.stringify(selected), setShowDelete],
  );

  const actions = (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Quota quota="clusters" label="clusters" />
      {supportsHostedClusters ? (
        <Quota quota="hostedClusters" label="hosted clusters" />
      ) : null}
      <AddClusterDialog organizationId={organizationId} />
    </Stack>
  );

  return (
    <Page title="Clusters">
      <ConfirmationDialog
        title="Rotate cluster token?"
        message="Rotating your cluster token invalidates the existing token. To continue using this cluster, reconfigure your cluster to use the new token."
        open={confirmRotate}
        onCancel={() => setConfirmRotate(false)}
        onConfirm={handleRotatingIdentity}
        variant="error"
      />
      <DisplayJWT
        open={!!newIdentity}
        jwt={newIdentity}
        onClose={() => setNewIdentity("")}
        title="Rotated token"
        message="Update your Pomerium configuration to use this new cluster token:"
      />
      <DeleteActionDialog
        name="clusters"
        open={showDelete}
        entries={
          clusters?.filter((r) => selected.includes(r.id)) || ([] as Entry[])
        }
        onClose={onCloseDeleteDialog}
        onDelete={handleMultiDelete}
      />
      <Popover
        anchorEl={anchorRef.current}
        disableScrollLock
        onClose={actionsPopover.handleClose}
        open={actionsPopover.open}
        slotProps={{
          paper: {
            sx: {
              minWidth: 260,
            },
          },
        }}
      >
        <MenuList>
          <MenuItem
            onClick={() =>
              navigate(
                "/app/settings?oid=" + organizationId + "&cid=" + clusterId,
              )
            }
          >
            Edit
          </MenuItem>
          <MenuItem onClick={deleteOne}>Delete</MenuItem>
          {anchorRef.current?.dataset.flavor === "standard" && (
            <MenuItem onClick={() => setConfirmRotate(true)}>
              Rotate Token
            </MenuItem>
          )}
        </MenuList>
      </Popover>
      <Header title="Clusters" actions={actions} />
      <ActionToolbarDialogs
        setShowDelete={setShowDelete}
        showDelete={showDelete}
        selected={
          clusters?.filter((c) => selected.includes(c.id)) || ([] as Entry[])
        }
      />
      <Container component={Paper} maxWidth={false} disableGutters={true}>
        <DataGridPro
          loading={isLoading || isFetching}
          getRowHeight={() => "auto"}
          rows={clusters || []}
          columns={columns}
          onRowSelectionModelChange={(model) => setSelected(model)}
          slots={{
            toolbar: CustomToolbar,
          }}
          columnVisibilityModel={JSON.parse(columnVisibilityModel)}
          onColumnVisibilityModelChange={(newModel) => {
            localStorage.setItem("clustersColumns", JSON.stringify(newModel));
            setColumnVisibilityModel(JSON.stringify(newModel));
          }}
          onColumnWidthChange={onColumnWidthChange}
        />
      </Container>
    </Page>
  );
};

type AddClusterDialogProps = {
  organizationId: string;
};

function AddClusterDialog({ organizationId }: AddClusterDialogProps) {
  const navigate = useNavigate();
  const [clusterName, setClusterName] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogStep, setDialogStep] = useState<"init" | "wizard">("init");
  const [createClusterResponse, setCreateClusterResponse] = useState<
    CreateClusterResponse | undefined
  >();

  const { count: clusterCount, limit: clusterLimit } = useQuota("clusters");
  const { count: hostedClusterCount, limit: hostedClusterLimit } =
    useQuota("hostedClusters");
  const {
    data: generatedSubdomainNameResponse,
    refetch: generateSubdomainName,
  } = useGenerateSubdomainNameQuery();
  const { data: organization } = useGetOrganizationQuery(organizationId);
  const { data: cluster } = useGetClusterQuery(
    {
      organizationId,
      clusterId: createClusterResponse?.id || "",
    },
    { skip: !organizationId || !createClusterResponse?.id },
  );
  const [addCluster, addClusterResult] = useCreateClusterMutation();
  const [updateCluster] = useUpdateClusterMutation();
  const [patchSettings] = usePatchSettingsMutation();
  const [clusterFlavor, setClusterFlavor] = useState<ClusterFlavor>("standard");
  const supportsHostedClusters = useFeatureFlag("hosted-clusters", false);

  useServerNotifications(organizationId);

  const isHostedSelection = clusterFlavor === "hosted";
  const selectedQuotaLimit = isHostedSelection
    ? hostedClusterLimit
    : clusterLimit;
  const selectedQuotaCount = isHostedSelection
    ? hostedClusterCount
    : clusterCount;
  const limitReached = selectedQuotaCount >= selectedQuotaLimit;

  // Check which cluster flavors have available quota
  const canCreateStandard = clusterCount < clusterLimit;
  const canCreateHosted =
    supportsHostedClusters && hostedClusterCount < hostedClusterLimit;
  const canCreateAnyCluster = canCreateStandard || canCreateHosted;

  useEffect(() => {
    if (!isDialogOpen) {
      setClusterName("");
      setDialogStep("init");
      setCreateClusterResponse(undefined);
      generateSubdomainName();
    }
  }, [isDialogOpen]);

  // Set default cluster flavor based on available quota when dialog opens
  useEffect(() => {
    if (isDialogOpen && supportsHostedClusters) {
      // If only one option is available, pre-select it
      if (canCreateHosted && !canCreateStandard) {
        setClusterFlavor("hosted");
      } else if (canCreateStandard && !canCreateHosted) {
        setClusterFlavor("standard");
      } else if (canCreateStandard) {
        // Both available, default to standard
        setClusterFlavor("standard");
      }
    }
  }, [
    isDialogOpen,
    canCreateStandard,
    canCreateHosted,
    supportsHostedClusters,
  ]);

  useEffect(() => {
    if (addClusterResult.isSuccess) {
      setCreateClusterResponse(addClusterResult.data);

      if (clusterFlavor === "standard") {
        setDialogStep("wizard");
      } else {
        setIsDialogOpen(false);
      }
    }
  }, [addClusterResult]);

  async function handleInitFinish() {
    if (addClusterResult.isUninitialized) {
      if (generatedSubdomainNameResponse) {
        await addCluster({
          organizationId,
          createClusterRequest: {
            name: clusterName,
            domain: generatedSubdomainNameResponse.name,
            flavor: clusterFlavor,
          },
        });
      }
    } else {
      if (addClusterResult.data) {
        updateCluster({
          organizationId,
          clusterId: addClusterResult.data.id,
          clusterProperties: {
            name: clusterName,
          },
        });
      }

      if (clusterFlavor === "standard") {
        setDialogStep("wizard");
      }
    }
  }

  function handleChangeAddressAndPort(
    address: string | undefined,
    port: string | undefined,
  ) {
    if (address && createClusterResponse) {
      updateCluster({
        organizationId,
        clusterId: createClusterResponse.id,
        clusterProperties: {
          name: createClusterResponse.name,
          manualOverrideIpAddress: address,
        },
      });
    }

    if (port && createClusterResponse) {
      patchSettings({
        organizationId,
        clusterId: createClusterResponse.id,
        jsonPatch: [
          {
            op: "replace",
            path: "./address",
            value: `:${port}`,
          },
        ],
      });
    }
  }

  const getCreateButtonTooltip = () => {
    // Check if both quotas are exhausted
    if (!canCreateStandard && !canCreateHosted && supportsHostedClusters) {
      return `All cluster quotas reached (Standard: ${clusterCount}/${clusterLimit}, Hosted: ${hostedClusterCount}/${hostedClusterLimit})`;
    }
    // Check if currently selected flavor has reached quota
    if (limitReached) {
      const quotaType = isHostedSelection ? "hosted cluster" : "cluster";
      return `${quotaType.charAt(0).toUpperCase() + quotaType.slice(1)} quota reached (${selectedQuotaCount}/${selectedQuotaLimit})`;
    }
    return "";
  };

  const createButtonTooltip = getCreateButtonTooltip();

  const getNewClusterButtonTooltip = () => {
    if (!canCreateStandard && !canCreateHosted && supportsHostedClusters) {
      return `All cluster quotas reached (Standard: ${clusterCount}/${clusterLimit}, Hosted: ${hostedClusterCount}/${hostedClusterLimit})`;
    }
    if (!canCreateStandard && !supportsHostedClusters) {
      return `Cluster quota reached (${clusterCount}/${clusterLimit})`;
    }
    return "";
  };

  const newClusterButtonTooltip = getNewClusterButtonTooltip();

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Tooltip
          title={newClusterButtonTooltip}
          disableHoverListener={canCreateAnyCluster}
        >
          <span>
            <Button
              variant="default"
              disabled={organization?.readOnly || !canCreateAnyCluster}
            >
              New Cluster
            </Button>
          </span>
        </Tooltip>
      </DialogTrigger>
      <DialogContent
        className={
          dialogStep === "wizard"
            ? "w-full h-fit bg-card text-card-foreground min-width-available"
            : ""
        }
      >
        {dialogStep === "init" && (
          <>
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Create a new Cluster</DialogTitle>
              <DialogDescription>Step 1: Initialize Cluster</DialogDescription>
            </DialogHeader>
            <span className="space-y-2">
              <Label htmlFor="clusterName">
                Cluster Name<span className="text-red-500">*</span>
              </Label>
              <Input
                id="clusterName"
                type="text"
                value={clusterName}
                placeholder="e.g. Homelab Applications"
                onChange={(e) => setClusterName(e.target.value)}
              />
            </span>
            {supportsHostedClusters ? (
              <RadioGroup
                value={clusterFlavor}
                onChange={(e) =>
                  setClusterFlavor(e.target.value as ClusterFlavor)
                }
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, auto)",
                  },
                  gap: 1,
                  justifyContent: "start",
                }}
              >
                <Tooltip
                  title={
                    !canCreateStandard
                      ? `Standard cluster quota reached (${clusterCount}/${clusterLimit})`
                      : ""
                  }
                  disableHoverListener={canCreateStandard}
                >
                  <span>
                    <FormControlLabel
                      value="standard"
                      control={<Radio />}
                      label="Self-Hosted"
                      disabled={!canCreateStandard}
                    />
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    !canCreateHosted
                      ? `Hosted cluster quota reached (${hostedClusterCount}/${hostedClusterLimit})`
                      : ""
                  }
                  disableHoverListener={canCreateHosted}
                >
                  <span>
                    <FormControlLabel
                      value="hosted"
                      control={<Radio />}
                      label="Hosted"
                      disabled={!canCreateHosted}
                    />
                  </span>
                </Tooltip>
              </RadioGroup>
            ) : null}
            <span className="space-y-2">
              <Label htmlFor="clusterSubdomain">Domain</Label>
              <div id="clusterSubdomain" className="flex flex-col gap-4">
                <code>
                  *.{generatedSubdomainNameResponse?.name || ""}.pomerium.app
                </code>
                <Button
                  variant="secondary"
                  onClick={generateSubdomainName}
                  disabled={!!createClusterResponse}
                >
                  Regenerate Subdomain
                </Button>
              </div>
            </span>
            <DialogFooter>
              <Tooltip
                title={createButtonTooltip}
                disableHoverListener={!limitReached}
              >
                <span>
                  <Button
                    variant="default"
                    disabled={
                      !clusterName || addClusterResult.isLoading || limitReached
                    }
                    onClick={async () => await handleInitFinish()}
                  >
                    {createClusterResponse ? "Update" : "Create"} Cluster
                  </Button>
                </span>
              </Tooltip>
            </DialogFooter>
          </>
        )}
        {dialogStep === "wizard" && (
          <ClusterOnboardingWizard
            cluster={cluster}
            clusterId={createClusterResponse?.id}
            clusterToken={createClusterResponse?.refreshToken}
            organizationId={organizationId}
            onFinishStep={() => {
              setIsDialogOpen(false);
              navigate(
                `/app/management/routes?oid=${organizationId}&cid=${createClusterResponse?.id}`,
              );
            }}
            handlePrevWizard={() => setDialogStep("init")}
            handleChangeAddressAndPort={handleChangeAddressAndPort}
            className="!border-none lg:max-h-[44rem] overflow-y-scroll"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default List;
